import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { overlayMotionAnchored } from './motion.ts'

/**
 * Popover — a generic anchored overlay on the Radix base
 * (`@radix-ui/react-popover`): a non-modal floating panel with outside-click /
 * `Esc` dismissal, full focus + ARIA (the trigger carries `aria-expanded`, the
 * panel is labelled by its own content), and popper positioning. It is the
 * substrate the Combobox and the date picker mount on, and it retires the
 * theme-customizer's hand-rolled popover. Compose: `Popover` (root, controlled
 * via `open`/`onOpenChange` or uncontrolled via `defaultOpen`) wrapping a
 * `PopoverTrigger` (use `asChild` to wrap your own control) and a
 * `PopoverContent`, which may hold an optional `PopoverHeader` and
 * `PopoverFooter` (full-bleed, hairline-divided) plus a `PopoverClose`.
 *
 * The panel chrome is tokens only: `--popover` surface, a `--border` hairline,
 * the shared `--shadow-overlay` lift composed with the mode-tuned
 * `--ring-inner-light` inner highlight, `rounded-xl`, and no arrow — it grows
 * from its trigger via the shared anchored overlay-motion contract
 * (`overlayMotionAnchored`), offset 8px. A bare (non-`asChild`) trigger pins to
 * `--bg-active` while open.
 *
 * Like the other overlay primitives (Dialog, Select, Tooltip), the content is
 * rendered in place rather than through a `Portal`, so the live `/styleguide`
 * route can snapshot the open panel (the snapshot reads each specimen's own DOM
 * subtree).
 */
function Popover(props: React.ComponentProps<typeof PopoverPrimitive.Root>) {
	return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
	className,
	...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
	return (
		<PopoverPrimitive.Trigger
			data-slot="popover-trigger"
			// A bare trigger pins to --bg-active while open; when `asChild` wraps a
			// styled control (a Button, the customizer pill) that control owns its
			// own look and this default is harmlessly merged onto it.
			className={cn('data-[state=open]:bg-[var(--bg-active)]', className)}
			{...props}
		/>
	)
}

function PopoverClose(
	props: React.ComponentProps<typeof PopoverPrimitive.Close>,
) {
	return <PopoverPrimitive.Close data-slot="popover-close" {...props} />
}

function PopoverContent({
	className,
	align = 'center',
	sideOffset = 8,
	...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
	return (
		<PopoverPrimitive.Content
			data-slot="popover-content"
			align={align}
			sideOffset={sideOffset}
			className={cn(
				// --popover surface + --border hairline + the overlay lift composed
				// with the mode-tuned inner top highlight; rounded-xl, no arrow.
				'bg-popover text-popover-foreground border-border shadow-[var(--shadow-overlay),var(--ring-inner-light)] z-50 w-72 rounded-xl border p-4 outline-hidden',
				overlayMotionAnchored,
				className,
			)}
			{...props}
		/>
	)
}

/** Optional full-bleed header row (title cluster) divided from the body. */
function PopoverHeader({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="popover-header"
			className={cn(
				'border-border -mx-4 -mt-4 mb-4 flex flex-col gap-1 border-b px-4 pt-4 pb-3',
				className,
			)}
			{...props}
		/>
	)
}

/** Optional full-bleed footer row (actions) divided from the body. */
function PopoverFooter({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="popover-footer"
			className={cn(
				'border-border -mx-4 -mb-4 mt-4 flex items-center justify-end gap-2 border-t px-4 pt-3 pb-4',
				className,
			)}
			{...props}
		/>
	)
}

export {
	Popover,
	PopoverTrigger,
	PopoverClose,
	PopoverContent,
	PopoverHeader,
	PopoverFooter,
}
